<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import V4Button from 'next/button/Button.vue';
import InputNext from 'dashboard/components-next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';
import Icon from 'next/icon/Icon.vue';
import WidgetCanvasCard from './WidgetCanvasCard.vue';
import WidgetSettingsPanel from './WidgetSettingsPanel.vue';
import ReportPickerDialog from './ReportPickerDialog.vue';

const props = defineProps({
  initialDashboard: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();
const store = useStore();

const reports = useMapGetter('wgptCustomReports/getCustomReports');

const name = ref('');
const description = ref('');
const widgets = ref([]);
const removedWidgetIds = ref([]);
const selectedIndex = ref(null);
const pickerRef = ref(null);
const pickerMode = ref('add'); // 'add' | 'change'

const selectedWidget = computed(() => {
  if (selectedIndex.value === null) return null;
  return widgets.value[selectedIndex.value] || null;
});

const widgetCountLabel = computed(() => {
  const count = widgets.value.length;
  if (count === 1) return t('WGPT_CUSTOM_DASHBOARDS.FORM.WIDGET_COUNT_ONE');
  return t('WGPT_CUSTOM_DASHBOARDS.FORM.WIDGET_COUNT_MANY', { count });
});

const canSubmit = computed(
  () =>
    name.value.trim().length > 0 &&
    widgets.value.length > 0 &&
    widgets.value.every(w => !!w.wgpt_custom_report_id)
);

let widgetSeq = 0;

function decorateWidget(widget) {
  const report = reports.value.find(
    r => r.id === Number(widget.wgpt_custom_report_id)
  );
  widgetSeq += 1;
  return {
    local_id: widget.local_id || `w_${widgetSeq}`,
    ...widget,
    report_config: widget.report_config || report?.config || {},
    report_name: widget.report_name || report?.name || '',
  };
}

function openPicker(mode = 'add') {
  pickerMode.value = mode;
  pickerRef.value?.open();
}

function onPickReport(report) {
  if (pickerMode.value === 'change' && selectedIndex.value !== null) {
    const current = widgets.value[selectedIndex.value];
    widgets.value[selectedIndex.value] = decorateWidget({
      ...current,
      wgpt_custom_report_id: report.id,
      report_config: report.config,
      report_name: report.name,
    });
    return;
  }
  widgets.value.push(
    decorateWidget({
      wgpt_custom_report_id: report.id,
      report_config: report.config,
      report_name: report.name,
      title: '',
      width: 'half',
      overrides: {
        time_range: '',
        time_granularity: '',
        dimension: '',
        visualization: '',
        value_mode: '',
      },
    })
  );
  nextTick(() => {
    selectedIndex.value = widgets.value.length - 1;
  });
}

function removeWidget(index) {
  const removed = widgets.value[index];
  if (removed?.id) removedWidgetIds.value.push(removed.id);
  widgets.value.splice(index, 1);
  if (selectedIndex.value === index) {
    selectedIndex.value = null;
  } else if (selectedIndex.value > index) {
    selectedIndex.value -= 1;
  }
}

function updateWidget({ field, value }) {
  if (selectedIndex.value === null) return;
  const widget = widgets.value[selectedIndex.value];
  if (!widget) return;
  if (field.startsWith('overrides.')) {
    const key = field.slice('overrides.'.length);
    widget.overrides = { ...(widget.overrides || {}), [key]: value };
  } else {
    widget[field] = value;
  }
}

function updateWidgetTitle(index, value) {
  if (widgets.value[index]) widgets.value[index].title = value;
}

function onDragEnd(event) {
  if (selectedIndex.value === null) return;
  const { oldIndex, newIndex } = event;
  if (oldIndex === newIndex) return;
  if (selectedIndex.value === oldIndex) {
    selectedIndex.value = newIndex;
  } else if (
    oldIndex < selectedIndex.value &&
    newIndex >= selectedIndex.value
  ) {
    selectedIndex.value -= 1;
  } else if (
    oldIndex > selectedIndex.value &&
    newIndex <= selectedIndex.value
  ) {
    selectedIndex.value += 1;
  }
}

function widgetGridClass(widget) {
  return widget.width === 'full' ? 'col-span-1 md:col-span-2' : 'col-span-1';
}

function buildPayload() {
  return {
    custom_dashboard: {
      name: name.value.trim(),
      description: description.value.trim(),
      layout: { columns: 2 },
      widgets: [
        ...widgets.value.map((w, idx) => ({
          id: w.id,
          wgpt_custom_report_id: Number(w.wgpt_custom_report_id),
          title: w.title?.trim() || '',
          width: w.width || 'half',
          position: idx,
          overrides: {
            time_range: w.overrides?.time_range || '',
            time_granularity: w.overrides?.time_granularity || '',
            dimension: w.overrides?.dimension || '',
            visualization: w.overrides?.visualization || '',
            value_mode: w.overrides?.value_mode || '',
          },
        })),
        ...removedWidgetIds.value.map(id => ({ id, _destroy: true })),
      ],
    },
  };
}

function onSubmit() {
  if (!canSubmit.value) return;
  emit('submit', buildPayload());
}

defineExpose({ submit: onSubmit, canSubmit });

function hydrate(dashboard) {
  name.value = dashboard.name || '';
  description.value = dashboard.description || '';
  removedWidgetIds.value = [];
  widgets.value = (dashboard.widgets || [])
    .slice()
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .map(w =>
      decorateWidget({
        id: w.id,
        wgpt_custom_report_id: w.wgpt_custom_report_id,
        report_config: w.report_config,
        report_name: w.report_name,
        title: w.title || '',
        width: w.width || 'half',
        overrides: {
          time_range: w.overrides?.time_range || '',
          time_granularity: w.overrides?.time_granularity || '',
          dimension: w.overrides?.dimension || '',
          visualization: w.overrides?.visualization || '',
          value_mode: w.overrides?.value_mode || '',
        },
      })
    );
  selectedIndex.value = widgets.value.length ? 0 : null;
}

onMounted(() => {
  if (!reports.value.length) {
    store.dispatch('wgptCustomReports/get');
  }
  hydrate(props.initialDashboard);
});

watch(
  () => props.initialDashboard,
  d => hydrate(d || {}),
  { deep: true }
);

// Reset overrides.dimension when the report changes (dimension may not exist on the new report)
watch(
  widgets,
  newWidgets => {
    newWidgets.forEach(w => {
      const report = reports.value.find(
        r => r.id === Number(w.wgpt_custom_report_id)
      );
      const dims =
        w.report_config?.dimensions || report?.config?.dimensions || [];
      const validValues = new Set([
        '',
        ...dims.map(d => d.property || d.source),
      ]);
      if (w.overrides?.dimension && !validValues.has(w.overrides.dimension)) {
        w.overrides.dimension = '';
      }
    });
  },
  { deep: true }
);
</script>

<template>
  <form class="flex flex-col gap-6" @submit.prevent="onSubmit">
    <div class="flex flex-col gap-4">
      <InputNext
        v-model="name"
        :label="t('WGPT_CUSTOM_DASHBOARDS.FORM.NAME')"
        :placeholder="t('WGPT_CUSTOM_DASHBOARDS.FORM.NAME_PLACEHOLDER')"
        required
      />
      <TextArea
        v-model="description"
        :label="t('WGPT_CUSTOM_DASHBOARDS.FORM.DESCRIPTION')"
        :placeholder="t('WGPT_CUSTOM_DASHBOARDS.FORM.DESCRIPTION_PLACEHOLDER')"
        rows="2"
      />
    </div>

    <div class="flex flex-col gap-3">
      <div class="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('WGPT_CUSTOM_DASHBOARDS.FORM.WIDGETS_TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{ t('WGPT_CUSTOM_DASHBOARDS.FORM.WIDGETS_DESCRIPTION') }}
          </p>
        </div>
        <div class="flex items-center gap-3">
          <span v-if="widgets.length" class="text-xs text-n-slate-11">
            {{ widgetCountLabel }}
          </span>
          <V4Button
            type="button"
            icon="i-lucide-plus"
            size="sm"
            :label="t('WGPT_CUSTOM_DASHBOARDS.FORM.ADD_WIDGET')"
            @click="openPicker('add')"
          />
        </div>
      </div>

      <div
        class="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] xl:grid-cols-[minmax(0,1fr)_360px] gap-4 items-start"
      >
        <div class="bg-n-surface-2 rounded-lg p-4 min-h-[420px]">
          <div
            v-if="!widgets.length"
            class="flex flex-col items-center justify-center text-center py-16"
          >
            <div
              class="flex items-center justify-center size-14 rounded-full bg-n-alpha-2 mb-4"
            >
              <Icon
                icon="i-lucide-layout-dashboard"
                class="size-7 text-n-slate-10"
              />
            </div>
            <h4 class="text-base font-medium text-n-slate-12 mb-1">
              {{ t('WGPT_CUSTOM_DASHBOARDS.FORM.CANVAS_EMPTY_TITLE') }}
            </h4>
            <p class="text-sm text-n-slate-11 max-w-sm mb-4">
              {{ t('WGPT_CUSTOM_DASHBOARDS.FORM.CANVAS_EMPTY_DESCRIPTION') }}
            </p>
            <V4Button
              type="button"
              icon="i-lucide-plus"
              :label="t('WGPT_CUSTOM_DASHBOARDS.FORM.ADD_FIRST_WIDGET')"
              @click="openPicker('add')"
            />
          </div>

          <Draggable
            v-else
            v-model="widgets"
            handle=".drag-handle"
            item-key="local_id"
            ghost-class="opacity-50"
            class="grid grid-cols-1 md:grid-cols-2 gap-3 auto-rows-min"
            @end="onDragEnd"
          >
            <template #item="{ element, index }">
              <div :class="widgetGridClass(element)">
                <WidgetCanvasCard
                  :widget="element"
                  :index="index"
                  :is-selected="selectedIndex === index"
                  @select="selectedIndex = index"
                  @remove="removeWidget(index)"
                  @update:title="updateWidgetTitle(index, $event)"
                />
              </div>
            </template>
          </Draggable>
        </div>

        <div class="lg:sticky lg:top-4">
          <WidgetSettingsPanel
            :widget="selectedWidget"
            :reports="reports"
            @update="updateWidget"
            @remove="selectedIndex !== null && removeWidget(selectedIndex)"
            @change-report="openPicker('change')"
          />
        </div>
      </div>
    </div>

    <ReportPickerDialog
      ref="pickerRef"
      :reports="reports"
      :exclude-report-id="
        pickerMode === 'change' ? selectedWidget?.wgpt_custom_report_id : null
      "
      @select="onPickReport"
    />
  </form>
</template>
